import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
} from "@mui/material";
import dayjs from "dayjs";

function parseBool(v) {
    if (v == null || v === "") return null;
    const s = String(v).trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(s)) return true;
    if (["false", "0", "no", "n"].includes(s)) return false;
    return "INVALID";
}

function parsePasted(text, separator) {
    const sep = separator === "tab" ? "\t" : separator; // "," eller ";"
    const lines = (text ?? "").split(/\r?\n/);

    const rows = [];
    const errors = [];

    lines.forEach((line, idx) => {
        const rowNo = idx + 1;
        const trimmed = line.trim();
        if (!trimmed) return;
        if (trimmed.startsWith("#")) return;

        const cols = line.split(sep).map((c) => c.trim());

        const date = cols[0];
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !dayjs(date, "YYYY-MM-DD", true).isValid()) {
            errors.push({ rowNo, message: "Ogiltigt datumformat (YYYY-MM-DD krävs)." });
            return;
        }

        const name = cols[1] || null;
        const comment = cols[2] || null;

        const b = parseBool(cols[3]);
        if (b === "INVALID") {
            errors.push({ rowNo, message: "Ogiltigt IsWorkingDay (true/false eller 1/0)." });
            return;
        }

        rows.push({
            date,
            name,
            comment,
            isWorkingDay: b ?? false, // default false = helgdag/undantag
        });
    });

    // dedupe: senaste vinner
    const map = new Map();
    rows.forEach((r) => map.set(r.date, r));
    const deduped = Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));

    return { rows: deduped, errors };
}

export default function PasteImportCalendarExceptionsDialog({
    open,
    onClose,
    onImport,          // async (items) => void
    isImporting,
    paperSx,
}) {
    const [separator, setSeparator] = useState("tab"); // tab | , | ;
    const [text, setText] = useState("");

    const parsed = useMemo(() => parsePasted(text, separator), [text, separator]);

    const handleImport = async () => {
        if (parsed.errors.length > 0) return;
        if (parsed.rows.length === 0) return;
        await onImport(parsed.rows);
        setText("");
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: paperSx }}>
            <DialogTitle>Klistra in undantagna dagar</DialogTitle>

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                            En rad per dag. Kolumner:
                            <b> Datum</b> (YYYY-MM-DD), <b>Namn</b> (valfri), <b>Kommentar</b> (valfri), <b>IsWorkingDay</b> (valfri).
                            <br />
                            Exempel (TAB):
                            <br />
                            <code>2026-12-25	Juldagen	Ledig	false</code>
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Typography variant="body2" sx={{ minWidth: 90 }}>Separator</Typography>

                        <ToggleButtonGroup
                            exclusive
                            value={separator}
                            onChange={(_, v) => v && setSeparator(v)}
                            size="small"
                        >
                            <ToggleButton value="tab">Tab</ToggleButton>
                            <ToggleButton value=",">,</ToggleButton>
                            <ToggleButton value=";">;</ToggleButton>
                        </ToggleButtonGroup>

                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Tips: klistrar du från Excel → välj “Tab”.
                        </Typography>
                    </Box>

                    <TextField
                        label="Klistra in här"
                        multiline
                        minRows={7}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        fullWidth
                    />

                    {parsed.errors.length > 0 && (
                        <Alert severity="error">
                            {parsed.errors.slice(0, 5).map((e, i) => (
                                <div key={i}>Rad {e.rowNo}: {e.message}</div>
                            ))}
                            {parsed.errors.length > 5 && <div>…{parsed.errors.length - 5} fler fel</div>}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Förhandsgranskning ({parsed.rows.length} rader)
                        </Typography>

                        <Box sx={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: 1, overflow: "hidden" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Datum</TableCell>
                                        <TableCell>Namn</TableCell>
                                        <TableCell>Kommentar</TableCell>
                                        <TableCell>IsWorkingDay</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {parsed.rows.slice(0, 50).map((r) => (
                                        <TableRow key={r.date}>
                                            <TableCell>{r.date}</TableCell>
                                            <TableCell>{r.name ?? ""}</TableCell>
                                            <TableCell>{r.comment ?? ""}</TableCell>
                                            <TableCell>{String(!!r.isWorkingDay)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {parsed.rows.length > 50 && (
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ opacity: 0.7 }}>
                                                Visar första 50 raderna…
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}>Avbryt</Button>
                <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={isImporting || parsed.rows.length === 0 || parsed.errors.length > 0}
                >
                    Importera
                </Button>
            </DialogActions>
        </Dialog>
    );
}
